import { toast } from "sonner";

import {
  MAINTENANCE_HEADER,
  MAINTENANCE_TOAST_DESCRIPTION,
  MAINTENANCE_TOAST_ID,
  MAINTENANCE_TOAST_TITLE,
  isMaintenanceResponse,
} from "@/lib/maintenance";

const TOAST_DURATION_MS = 15_000;

/**
 * Shows the maintenance notice. The fixed toast id makes sonner update one
 * toast in place, so a burst of failed mutations never stacks copies.
 */
export const showMaintenanceToast = (): void => {
  toast(MAINTENANCE_TOAST_TITLE, {
    id: MAINTENANCE_TOAST_ID,
    description: MAINTENANCE_TOAST_DESCRIPTION,
    duration: TOAST_DURATION_MS,
  });
};

/**
 * Inspects a response for the maintenance refusal and shows the notice. Never
 * throws and never consumes the body the tRPC client is about to read.
 */
export const noteMaintenanceFromResponse = async (
  response: Response,
): Promise<void> => {
  if (response.status !== 503) return;

  // Only clone when the header is absent — the header is the normal path.
  let bodyText: string | undefined;
  if (response.headers.get(MAINTENANCE_HEADER) !== "1") {
    try {
      bodyText = await response.clone().text();
    } catch (error) {
      console.warn("[maintenance] could not read the 503 body", error);
      return;
    }
  }

  if (isMaintenanceResponse(response, bodyText)) showMaintenanceToast();
};

/**
 * `fetch` for the browser httpBatchLinks. Returns the response untouched, so
 * each mutation's own error handling runs exactly as before; network errors
 * reject as before and are never treated as maintenance.
 */
export const maintenanceAwareFetch = async (
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> => {
  const response = await fetch(input, init);

  try {
    await noteMaintenanceFromResponse(response);
  } catch (error) {
    // Presentation only: a notice that cannot show must never fail the request.
    console.warn("[maintenance] could not show the notice", error);
  }

  return response;
};
